import React from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from './components/ui/toaster';

// Contexts
import { AuthProvider, useAuth } from './contexts/AuthContext';

// Components
import Navigation from './components/Navigation';
import ScrollToTop from './components/ScrollToTop';

// Landing Pages
import Home from './pages/Home';
import Technology from './pages/Technology';
import Features from './pages/Features';
import Pricing from './pages/Pricing';
import About from './pages/About';
import Ethics from './pages/Ethics';

// Test Pages
import AvatarTest from './pages/AvatarTest';

// Auth
import Auth from './pages/Auth';

// Presence Management (Clean Architecture)
import PresenceCreation from './pages/PresenceCreation';
import PresenceListPage from './pages/presence/PresenceListPage';
import PresenceLayout from './pages/presence/PresenceLayout';
import ChatPage from './pages/presence/ChatPage';
import ConsentPage from './pages/presence/ConsentPage';
import ActivityPage from './pages/presence/ActivityPage';
import SettingsPage from './pages/presence/SettingsPage';
import MemoryPage from './pages/presence/MemoryPage';
import TrainingPage from './pages/presence/TrainingPage';

// User Settings
import ProfilePage from './pages/settings/ProfilePage';

// Protected Route Component
function ProtectedRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-cyan-400">Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/auth" />;
  }

  return children;
}

// Main App Content
function AppContent() {
  const { isAuthenticated } = useAuth();

  return (
    <div className="App">
      <ScrollToTop />
      {!window.location.pathname.includes('/auth') && <Navigation />}
      <div className={!window.location.pathname.includes('/auth') && !window.location.pathname.includes('/presence/') ? 'pt-20' : ''}>
        <Routes>
        {/* Test Pages */}
        <Route path="/avatar-test" element={<AvatarTest />} />

        {/* Landing Pages */}
        <Route path="/" element={<Home />} />
        <Route path="/technology" element={<Technology />} />
        <Route path="/features" element={<Features />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/about" element={<About />} />
        <Route path="/ethics" element={<Ethics />} />

        {/* Auth - Redirect after login */}
        <Route path="/auth" element={isAuthenticated ? <Navigate to="/presences" /> : <Auth />} />

        {/* Create Presence */}
        <Route path="/create" element={
          <ProtectedRoute>
            <PresenceCreation />
          </ProtectedRoute>
        } />

        {/* Presence List - Main Dashboard */}
        <Route path="/presences" element={
          <ProtectedRoute>
            <PresenceListPage />
          </ProtectedRoute>
        } />

        {/* Presence Workspace (5 modules) */}
        <Route path="/presence/:presenceId" element={
          <ProtectedRoute>
            <PresenceLayout />
          </ProtectedRoute>
        }>
          <Route index element={<Navigate to="chat" replace />} />
          <Route path="chat" element={<ChatPage />} />
          <Route path="activity" element={<ActivityPage />} />
          <Route path="memory" element={<MemoryPage />} />
          <Route path="consent" element={<ConsentPage />} />
          <Route path="settings" element={<SettingsPage />} />
          <Route path="training" element={<TrainingPage />} />
        </Route>

        {/* User Profile */}
        <Route path="/settings/profile" element={
          <ProtectedRoute>
            <ProfilePage />
          </ProtectedRoute>
        } />

        {/* Fallback - Redirect to presences or home */}
        <Route path="*" element={
          isAuthenticated ? <Navigate to="/presences" /> : <Navigate to="/" />
        } />
        </Routes>
      </div>
      <Toaster />
    </div>
  );
}

// App Root
function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
